/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button";
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../../shared-components/forms/NumberedHeading";
import ImageAttribution from '../../shared/ImageAttribution';
// data
import { ReviewData, MediaObject } from "../../../data/Defaults";

export default function MissionSelectionArray({ control, register, setValue, getValues, errors, inputDir }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion defaultExpanded className="bg-transparent shadow-none">
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold" sx={{lineHeight: '28px'}}>
                      Difficulty {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >

                      <Controller
                        control={control}
                        name={`content.core.${index}.id`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`content.core.${index}.review.status`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`content.core.${index}.review.approved`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`content.core.${index}.review.approval_date`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <NumberedHeading number="1" title="Difficulty" instructions='Select either "Base", "Intermediate", or "Advanced".' required />
                      <Controller
                        control={control}
                        name={`content.core.${index}.difficulty`}
                        render={({ field }) => (
                          <FormControl fullWidth className='mt-16' size="small" error={!!errors.content?.core?.[index]?.difficulty} required>
                            <InputLabel id="select-label">Difficulty</InputLabel>
                            <Select
                              {...field}
                              labelId="select-tag"
                              id="difficulty"
                              label="Difficulty"
                              classes={{ select: "flex items-center space-x-12" }}
                              defaultValue=""
                            >
                              <MenuItem value="" className="space-x-12" disabled>
                                Select a Tag
                              </MenuItem>
                              <MenuItem value="base" className="space-x-12">
                                Base
                              </MenuItem>
                              <MenuItem value="intermediate" className="space-x-12">
                                Intermediate
                              </MenuItem>
                              <MenuItem value="advanced" className="space-x-12">
                                Advanced
                              </MenuItem>
                            </Select>
                            {!!errors.content?.core?.[index]?.tag ? (
                              <FormHelperText>{errors.content?.core?.[index]?.difficulty?.message}</FormHelperText>
                            ) : null}
                          </FormControl>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="2" title="Image" instructions="Add Image Attribution." required />
                      <Controller 
                        control={control}
                        name={`content.core.${index}.media`}
                        render={({ field }) => <ImageAttribution targetObj={`content.core.${index}.media`} errors={errors.content?.core?.[index]?.media} inputDir={inputDir} {...{control }} />}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="Description" instructions="Enter the description for the difficulty level." required />
                      <Controller
                        control={control}
                        name={`content.core.${index}.description`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Description"
                            placeholder="Description"
                            error={!!errors.content?.core?.[index]?.description}
                            helperText={errors.content?.core?.[index]?.description?.message}
                            variant="outlined"  
                            fullWidth
                            multiline
                            minRows={5}
                            maxRows={10}
                            required
                          />
                        )}
                      />

                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            id: BASEUtils.generateGUID(),
            review: ReviewData,
            difficulty: "",
            media: MediaObject,
            description: "",
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Difficulty
          </span>
        </Button>
      </Box>
    </>
  );
}
